import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useController, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { styled, useTheme } from "@mui/material/styles";
import { error } from "app/themes/colors";
import SortableFileRow from "./sortable-file-row.component";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { formatBytes } from "utils/common";

const acceptedTypeMap = {
  "application/pdf": "PDF",
};

interface FileUploadProps {
  name: string;
  label?: string;
  acceptedType?: keyof typeof acceptedTypeMap;
  fileSizeLimit: number;
  fileCountLimit?: number;
  totalFilesSizeLimit?: number;
  setUploadWarningMessage: React.Dispatch<React.SetStateAction<string>>;
}

export default function FileUpload({
  name,
  label = "Upload a File",
  acceptedType,
  fileCountLimit,
  totalFilesSizeLimit,
  fileSizeLimit,
  setUploadWarningMessage,
}: FileUploadProps) {
  const {
    setValue,
    control,
    formState: { isSubmitting, errors },
    trigger,
  } = useFormContext();
  const theme = useTheme();
  const acceptedTypeTitle = acceptedType ? acceptedTypeMap[acceptedType] : "";

  const { field } = useController({ name, control });
  const [filesList, setFilesList] = useState<File[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const onDragEnter = () => wrapperRef.current?.classList.add("dragover");
  const onDragLeave = () => wrapperRef.current?.classList.remove("dragover");

  const onFileDrop = useCallback(
    (e: React.SyntheticEvent<EventTarget>) => {
      const target = e.target as HTMLInputElement;
      if (!target.files) return;

      const newFilesList = Object.values(target.files);

      for (let file of newFilesList) {
        if (acceptedType && file.type !== acceptedType) {
          const fileTypeMessage = acceptedTypeTitle
            ? `${acceptedTypeTitle} files`
            : "files";

          setUploadWarningMessage(
            `Warning! Please only upload ${fileTypeMessage} files that are under ${formatBytes(
              fileSizeLimit
            )}.`
          );
          break;
        }

        if (file.size > fileSizeLimit) {
          setUploadWarningMessage(
            `Warning! Please upload files that are under ${formatBytes(
              fileSizeLimit
            )}.`
          );
          break;
        }

        if (totalFilesSizeLimit && file.size > totalFilesSizeLimit) {
          setUploadWarningMessage(
            `Warning! Please upload files that are under ${formatBytes(
              totalFilesSizeLimit
            )}.`
          );
          break;
        }
      }

      if (totalFilesSizeLimit) {
        const filesTotalSize = [
          ...newFilesList,
          ...Object.values(filesList),
        ].reduce((acc, currentFile) => acc + currentFile.size, 0);

        if (filesTotalSize > totalFilesSizeLimit) {
          setUploadWarningMessage(
            `Warning! Please upload no more than ${formatBytes(
              totalFilesSizeLimit
            )} of files at a time.`
          );
        }
      }

      if (fileCountLimit) {
        if (newFilesList.length + filesList.length > fileCountLimit) {
          setUploadWarningMessage(
            `Warning! Please upload a maximum of ${fileCountLimit} files at a time.`
          );
        }
      }

      setFilesList((prevFiles) => [...prevFiles, ...newFilesList]);
      field.onChange([...filesList, ...newFilesList]);
      trigger(name);
    },
    [
      field,
      name,
      trigger,
      filesList,
      acceptedType,
      setUploadWarningMessage,
      acceptedTypeTitle,
      fileCountLimit,
      fileSizeLimit,
      totalFilesSizeLimit,
    ]
  );

  const removeFile = useCallback(
    (index: number) => {
      setFilesList((prevFiles) => {
        const newFilesList = prevFiles.toSpliced(index, 1);

        setValue(name, newFilesList);

        if (newFilesList.length <= 10) {
          setUploadWarningMessage("");
        }

        return newFilesList;
      });

      trigger(name);
    },
    [name, setValue, trigger, setUploadWarningMessage]
  );

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setFilesList((prevRows: File[]) => {
        setValue(
          name,
          update(prevRows, {
            $splice: [
              [dragIndex, 1],
              [hoverIndex, 0, prevRows[dragIndex] as File],
            ],
          })
        );
        return update(prevRows, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevRows[dragIndex] as File],
          ],
        });
      });
    },
    [name, setValue]
  );
  useEffect(() => {
    if (field.value && filesList.length === 0) {
      setFilesList(field.value);
    }
  }, [field.value, filesList]);

  useEffect(() => {
    if (isSubmitting) {
      setFilesList([]);
    }
  }, [isSubmitting]);
  return (
    <>
      <CustomBox>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: "relative",
            minWidth: "300px",
            width: "100%",
            height: "6rem",
            border: `1px dashed ${theme.palette.secondary.main}`,
            borderRadius: "12px",
          }}
          ref={wrapperRef}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDragLeave}
        >
          <Stack justifyContent="center" sx={{ p: 1, textAlign: "center" }}>
            <div>
              <FileDownloadIcon />
            </div>
            <Typography variant="body1" component="span">
              {label}
            </Typography>
          </Stack>
          <Controller
            name={name}
            defaultValue=""
            control={control}
            render={({ field: { name, onBlur, ref } }) => (
              <input
                type="file"
                multiple
                name={name}
                onBlur={onBlur}
                ref={ref}
                onChange={onFileDrop}
                accept={acceptedType}
                style={{
                  opacity: 0,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
              />
            )}
          />
        </Box>
      </CustomBox>
      <FormHelperText sx={{ textAlign: "center", my: 1, color: error[700] }}>
        <>{errors && errors[name] ? errors[name]?.message : ""}</>
      </FormHelperText>
      <DndProvider backend={HTML5Backend}>
        {filesList.length > 0 ? (
          <ol>
            <Stack
              spacing={2}
              sx={{
                my: 2,
                minWidth: "300px",
                minHeight: "50px",
                maxHeight: "150px",
              }}
            >
              {filesList.map((item, index) => {
                return (
                  <SortableFileRow
                    file={item}
                    index={index}
                    moveRow={moveRow}
                    id={item?.name + index}
                    key={item?.name + index}
                    removeFile={removeFile}
                  />
                );
              })}
            </Stack>
          </ol>
        ) : null}
      </DndProvider>
    </>
  );
}

const CustomBox = styled(Box)(({ theme }) => ({
  "&.MuiBox-root": {
    backgroundColor: theme.palette.common.white,
    borderRadius: "12px",
    boxShadow: `${theme.palette.common.black}19 0px 8px 24px`,
    padding: "8px",
  },
  "&.MuiBox-root:hover, &.MuiBox-root.dragover": {
    opacity: 0.6,
  },
}));
